import React, { useCallback, useEffect, memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { useRefresh } from 'utils/hooks/useRefresh'
import { FacilityAutosuggest } from 'apps/fleet-planner/components/atoms/FacilityAutosuggest'
import { useFacilityInputState } from 'apps/fleet-planner/hooks/useFacilityInputState'
import { ICustomerContact } from 'apps/fleet-planner/types'
import { IStop } from 'apps/fleet-planner/types/loads'
import { IFacility, IFacilityAddress } from 'apps/fleet-planner/utils/api'
import { recentFacilitiesStateSelector } from 'apps/fleet-planner/utils/selectors/facilities'
import { TextInputIconType } from 'components/atoms'
import { getEntityById } from 'utils/getEntityById'
import 'assets/scss/5_components/_suggest-facility.scss'

export interface FacilitySuggestion extends ILocationSuggestion {
  name?: string
  id?: string
  addressLine1?: string
  addressLine2?: string
  countryCode?: string
}

// Using `Pick` here because these values are tied to
// `IStop` use of legacy address model. When it becomes
// deprecated, `city` and `stateCode` can be removed
export interface IFacilityOption extends Pick<IStop, 'city' | 'stateCode'> {
  address?: Partial<IFacilityAddress>
  facility?: Partial<IFacility>
  facilityContact?: ICustomerContact
}

interface ISuggestFacilityProps {
  placeholder?: string
  initialValue?: FacilitySuggestion
  defaultValue?: FacilitySuggestion
  value?: FacilitySuggestion
  id?: string
  required?: boolean
  label?: string
  countryCodes?: string
  locationTypes?: string
  errorMessage?: string
  validationMessageLabel?: string
  large?: boolean
  dataTest?: string
  invalid?: boolean
  /**
   * @default 'search'
   */
  icon?: keyof typeof TextInputIconType

  onChangeFacility: (object: {
    address?: Partial<IFacilityAddress>
    facility?: Partial<IFacility>
  }) => void
  onCreateFacility: (inputValue?: string) => void
  onCreateAddress: (object: {
    address?: Partial<IFacilityAddress>
    facility?: Partial<IFacility>
  }) => void
  onClearFacility: () => void
  onBlur?: () => void
  disabled?: boolean
  onValidationChange?: (val?: string) => void
}

const SuggestFacility = memo<ISuggestFacilityProps>(
  ({
    dataTest,
    disabled: disabledProp,
    errorMessage,
    icon: iconProp,
    large: largeProp,
    onBlur: onBlurProp,
    onChangeFacility: onChangeFacilityProp,
    onClearFacility: onClearFacilityProp,
    onCreateFacility: onCreateFacilityProp,
    onValidationChange: onValidationChangeProp,
    placeholder = 'Enter a Facility or City',
    required: requiredProp,
    value,
  }) => {
    const { facilities } = useSelector(recentFacilitiesStateSelector)
    const { getErrorMessage, onBlurTrigger, onChangeTrigger } = useFacilityInputState()

    // Re-mount <FacilityAutosuggest /> when value changes
    // to initialize internal state
    const [inputValue, setInputValue] = useState<string | undefined>('')
    const { key, resetKey } = useRefresh()
    useEffect(() => {
      if (value && !!value?.name) {
        resetKey()
      }
    }, [resetKey, value])

    /**
     * Handles change from <Autosuggest />
     */
    const changeHandler = useCallback(
      (change: Partial<IFacility> | undefined): void => {
        const facility = getEntityById(facilities, change?.id)

        // Clear (clicking x) works to remove this instead
        if (!facility) return

        const { addresses, id: facilityId, name: facilityName } = facility || {}
        const {
          addressLine1,
          addressLine2,
          countryCode,
          id: addressId,
          locality,
          postalCode,
          purpose,
          subdivisionCode,
        } = addresses[0] || {}

        setInputValue(facilityName || '')
        onChangeTrigger()

        if (onChangeFacilityProp) {
          onChangeFacilityProp({
            facility: {
              id: facilityId,
              name: facilityName,
            },
            address: {
              id: addressId,
              addressee: facilityName,
              addressLine1,
              addressLine2,
              locality,
              subdivisionCode,
              countryCode,
              postalCode,
              purpose,
            },
          })
        }
      },
      [facilities, onChangeFacilityProp, onChangeTrigger]
    )

    const addEntityHandler = (val: string = '') => onCreateFacilityProp(val)

    const error = getErrorMessage({
      inputValue,
      value,
    })

    // When error updates, send to parent component
    useEffect(() => {
      if (onValidationChangeProp) onValidationChangeProp(error)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      onChangeTrigger()
    }

    const blurHandler = useCallback(() => {
      onBlurTrigger()
      if (onBlurProp) onBlurProp()
    }, [onBlurProp, onBlurTrigger])

    return (
      <div className='suggest-facility'>
        <FacilityAutosuggest
          key={key}
          placeholder={placeholder}
          value={value}
          onChange={changeHandler}
          onAddEntity={addEntityHandler}
          onCancel={onClearFacilityProp}
          error={errorMessage || error}
          disabled={disabledProp}
          dataTest={dataTest}
          fetchList={false}
          onInputChange={inputChangeHandler}
          onBlur={blurHandler}
          required={requiredProp}
          large={largeProp}
          icon={iconProp}
        />
      </div>
    )
  }
)

export default SuggestFacility
